/**
 * VoiceSavedBanner — "This week your team voice-saved 2h 14m of typing."
 *
 * Tiny dopamine hit at the top of the Admin Dashboard. Pulls
 * `typing_seconds_saved` from /admin/voice-analytics/summary?days=7 and
 * formats it as Xh Ym. Hides itself if there is nothing to brag about yet.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, ArrowRight, Mic } from "lucide-react";

function fmtDuration(totalSeconds) {
  const s = Math.max(0, Math.round(Number(totalSeconds) || 0));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export default function VoiceSavedBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/admin/voice-analytics/summary?days=7")
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || !data) return null;
  // Hide if there's nothing meaningful yet (< 1 min saved AND no events)
  if ((data.typing_seconds_saved || 0) < 60 && (data.total_events || 0) === 0) return null;

  const duration = fmtDuration(data.typing_seconds_saved);
  const events = data.total_events || 0;
  const delta = data.delta_pct;

  return (
    <Link
      to="/admin/analytics"
      className="group block rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 via-pink-50 to-cyan-50 p-4 sm:p-5 transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-400"
      data-testid="voice-saved-banner"
    >
      <div className="flex items-center gap-3">
        <span className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-sm">
          <Mic className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow !text-purple-700 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Voice ROI this week
          </div>
          <div className="mt-0.5 font-heading text-base sm:text-lg font-semibold text-clinical-text leading-snug">
            Your team{" "}
            <span className="text-purple-700" data-testid="voice-saved-duration">
              voice-saved {duration}
            </span>
            {" "}of typing
            {events > 0 && (
              <span className="text-clinical-subtext font-normal">
                {" "}· {events} voice {events === 1 ? "note" : "notes"}
              </span>
            )}
            {delta != null && delta > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                +{delta}% vs last week
              </span>
            )}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-purple-500 group-hover:translate-x-0.5 transition" />
      </div>
    </Link>
  );
}
